import { API, graphqlOperation } from 'aws-amplify';
import { apiQuery } from './base';
import { listMedicationLogs, listWellnessLogs } from '../graphql/queries';

// Helper function to normalize medications array format
const normalizeMedications = (medications) => {
  if (!medications || !Array.isArray(medications)) {
    return [];
  }

  return medications.map(med => {
    // Handle DynamoDB format: { "S": "medication string" }
    if (typeof med === 'object' && med !== null && med.S) {
      return med.S;
    }
    // Handle direct string format
    if (typeof med === 'string') {
      return med;
    }
    // Fallback for unexpected formats
    return String(med);
  });
};

const MAX_PAGES = 1000;

// Fetches every page of a paged list query. Keeps going while AppSync returns a
// nextToken, even when a page comes back empty - filtered scans often do that,
// and stopping on an empty page would silently drop the rest of the results.
const fetchAllPages = async (query, queryName, modifiers, resolver) => {
  const batchSize = 100;
  let nextToken = null;
  let allItems = [];
  let pages = 0;

  do {
    let response;
    try {
      response = await API.graphql({
        ...graphqlOperation(query, { ...modifiers, limit: batchSize, nextToken }),
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      });
    } catch (error) {
      // Even if there's an error, check if we got partial data
      if (error.data && error.data[queryName]) {
        console.warn(`${queryName}: GraphQL error but got partial data, continuing:`, error.errors);
        response = error;
      } else {
        console.error(`${queryName}: complete API failure:`, error);
        break;
      }
    }

    if (!response.data || !response.data[queryName]) {
      console.error(`${queryName}: invalid response structure:`, response);
      break;
    }

    const { items, nextToken: responseNextToken } = response.data[queryName];
    allItems = allItems.concat((items || []).filter((item) => !!item));

    if (response.errors && response.errors.length > 0) {
      console.warn(`${queryName}: batch had ${response.errors.length} GraphQL errors, nextToken:`, responseNextToken);
    }

    // Stop if the token never advances, so a misbehaving response can't loop forever
    if (responseNextToken === nextToken) break;
    nextToken = responseNextToken;
    pages += 1;
  } while (nextToken && pages < MAX_PAGES);

  resolver(allItems);
};

const getMedicationLogsWithErrorHandling = (modifiers, resolver) =>
  fetchAllPages(listMedicationLogs, 'listMedicationLogs', modifiers, resolver);

const mapLogs = (wellnessLogs, medicationLogs, appUsers, resolver) => {
  const logs = wellnessLogs
    .filter((log) => {
      if (!log || !log.appUserID) return false;
      const { appUserID } = log;
      return appUsers.some((user) => user && user.id === appUserID);
    })
    .map((log) => {
      const { appUserID, id, owner, ...logNoID } = log;
      const appUser = appUsers.find((user) => user && user.id === appUserID);

      if (!appUser) {
        console.warn(`No app user found for appUserID: ${appUserID}`);
        return null;
      }

      const { username } = appUser;

      // Find medication log, being careful about potential null/undefined values
      const medicationLog = medicationLogs.find((medLog) =>
        medLog && medLog.id && medLog.id === log.id
      );

      const fullLog = {
        username: username || 'Unknown',
        medications: normalizeMedications(medicationLog?.medications),
        ...logNoID,
      };
      return fullLog;
    })
    .filter(log => log !== null); // Remove any null entries from failed mappings

  resolver(logs);
};

function splitArray(inputArray, chunkSize) {
  const result = [];
  for (let i = 0; i < inputArray.length; i += chunkSize) {
    result.push(inputArray.slice(i, i + chunkSize));
  }
  return result;
}

export const getWellnessLogsForUsers = async (appUsers, setWellnessLogs) => {
  const appUserFilters = appUsers.map((user) => {
    return { appUserID: { eq: user.id } };
  });
  // AppSync/DynamoDB filter expressions have a size limit, so the group's users
  // are chunked into multiple `or`-composed filters rather than one giant filter
  // (or, worse, no filter at all - which used to download the entire table).
  const filterSets = splitArray(appUserFilters, 20);
  let wellnessLogs = [];
  let medicationLogs = [];

  try {
    await Promise.all(
      filterSets.map((filterSet) =>
        fetchAllPages(listWellnessLogs, 'listWellnessLogs', { filter: { or: filterSet } }, (data) => {
          wellnessLogs = wellnessLogs.concat(data);
        })
      )
    );

    // Fetch medication logs with enhanced error handling, scoped the same way
    await Promise.all(
      filterSets.map((filterSet) =>
        getMedicationLogsWithErrorHandling({ filter: { or: filterSet } }, (data) => {
          medicationLogs = medicationLogs.concat(data);
        })
      )
    );

    mapLogs(wellnessLogs, medicationLogs, appUsers, setWellnessLogs);
  } catch (e) {
    console.log('Error in getWellnessLogsForUsers:', e);
    // Continue processing with what we have
    mapLogs(wellnessLogs, medicationLogs, appUsers, setWellnessLogs);
  }
};

export const getWellnessLogsForUser = (appUserID, setWellnessLogs) => {
  return apiQuery(listWellnessLogs, { filter: { appUserID: { eq: appUserID } } }, setWellnessLogs);
};
